import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import PageHero from "@/components/PageHero";
import Marquee from "@/components/Marquee";
import { Reveal, Overline } from "@/components/motion";
import { MANIFESTO } from "@/data/site";

export default function About() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [-60, 60]);

  return (
    <main data-testid="about-page">
      <PageHero
        testid="about-hero"
        overline="About Tuskers Grey Investments"
        lines={["Rooted in the Midlands.", "Built for the trade."]}
        intro="Tuskers Grey Investments is the dedicated mining and mine product brokerage of the Tuskers Grey family — nothing else, by design. From our base in Midlands Province, we move the Great Dyke's wealth to buyers who demand proof."
      />

      <section data-testid="about-story" className="mx-auto max-w-7xl px-6 pb-28 lg:px-10">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div ref={imgRef} className="relative overflow-hidden rounded-[2rem] border border-white/10 lg:col-span-6">
            <motion.img
              style={{ y: yImg }}
              src="/assets/img/zimbabwe.jpg"
              alt="Golden savanna of Zimbabwe's Midlands at sunset"
              className="h-[420px] w-full scale-110 object-cover lg:h-[540px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950/70 via-transparent to-transparent" />
            <p className="absolute bottom-6 left-6 text-[11px] uppercase tracking-[0.35em] text-gold-light">
              Midlands Province · Zimbabwe
            </p>
          </div>
          <div className="lg:col-span-6">
            <Reveal>
              <Overline>Who we are</Overline>
              <h2 className="mt-5 font-serif text-3xl leading-tight tracking-tight text-slate-50 sm:text-4xl">
                Brokers who know the ground <em className="gold-text not-italic">beneath the deal.</em>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-7 text-base leading-relaxed text-slate-400">
                The Midlands sits astride the Great Dyke — one of the richest mineral corridors on
                earth. Gold, chrome, PGMs, nickel and lithium all come out of this ground, and so
                do we. Tuskers Grey Investments exists for one purpose: brokering minerals and mine
                products from this province to the world.
              </p>
              <p className="mt-5 text-base leading-relaxed text-slate-400">
                We are not generalists. Every relationship we hold — with small-scale producers,
                primary mines, laboratories, transporters and buyers — serves the mineral trade
                alone. That focus is our edge, and our clients' protection.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-9 grid grid-cols-2 gap-6">
                {[["Strictly mining", "No side trades — minerals and mine products only"], ["Great Dyke network", "Producers, labs and transporters on speed dial"]].map(([t, b]) => (
                  <div key={t} className="rounded-2xl border border-white/10 bg-navy-900/60 p-6">
                    <p className="font-serif text-lg text-gold-light">{t}</p>
                    <p className="mt-2 text-sm leading-relaxed text-slate-500">{b}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee slow items={["Integrity at the pit", "Proof before payment", "Pit to port", "Midlands to the world"]} />

      <section data-testid="about-principles" className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
        <Reveal>
          <Overline>How we work</Overline>
          <h2 className="mt-5 max-w-2xl font-serif text-3xl tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
            The chapters we trade by.
          </h2>
        </Reveal>
        <div className="mt-16">
          {MANIFESTO.map((ch, i) => (
            <Reveal key={ch.n} delay={i * 0.06}>
              <div
                data-testid={`about-chapter-${ch.n}`}
                className="group grid gap-5 border-t hairline-gold py-10 lg:grid-cols-12 lg:items-baseline"
              >
                <span className="font-serif text-4xl text-gold/30 transition-colors duration-500 group-hover:text-gold lg:col-span-2 lg:text-5xl">
                  {ch.n}
                </span>
                <h3 className="font-serif text-xl text-slate-100 sm:text-2xl lg:col-span-4">{ch.title}</h3>
                <p className="max-w-xl text-sm leading-relaxed text-slate-400 lg:col-span-6">{ch.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
